import { useEffect, useRef, useState } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { useAppStore } from './store/appStore'
import { SocketProvider } from './hooks/useSocket'
import { isDevBuild, showConnectionDebug } from '@/config/runtimeFlags'
import { sendVisitTelemetry } from '@/utils/telemetry'
import { initializeRuntimeMetrics } from '@/utils/runtimeMetrics'

// Components
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Terminal from './pages/Terminal'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import ReportDetails from './pages/ReportDetails'
import LoadingScreen from './components/LoadingScreen'
import Login from './pages/Login'
import AcceptableUse from './pages/AcceptableUse'
import Targets from './pages/Targets'
import Usage from './pages/Usage'
import Admin from './pages/Admin'

function RequireAuth({ children }: { children: JSX.Element }) {
  const { isAuthenticated } = useAppStore()
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return children
}

function App() {
  const { isConnected, connectionError, initialize } = useAppStore()
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()
  const logout = useAppStore(s => s.logout)
  const isAuthenticated = useAppStore(s => s.isAuthenticated)
  const telemetryConsent = useAppStore(s => s.telemetryConsent)
  const [showSessionExpired, setShowSessionExpired] = useState(false)
  const sessionExpiredRef = useRef(false)

  useEffect(() => {
    // Initialize the application
    const initApp = async () => {
      try {
        await initialize()
      } catch (error) {
        console.error('Failed to initialize app:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initApp()
  }, [initialize])

  useEffect(() => {
    initializeRuntimeMetrics()
  }, [])

  // Global unauthorized handler: on 401/403, log the user out and send them to login
  useEffect(() => {
    const onUnauthorized = () => {
      logout()
      if (!sessionExpiredRef.current) {
        sessionExpiredRef.current = true
        setShowSessionExpired(true)
      }
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true })
      }
    }
    window.addEventListener('app:unauthorized', onUnauthorized)
    return () => window.removeEventListener('app:unauthorized', onUnauthorized)
  }, [location.pathname, logout, navigate])

  useEffect(() => {
    if (isAuthenticated) {
      sessionExpiredRef.current = false
      setShowSessionExpired(false)
    }
  }, [isAuthenticated])

  const handleSessionExpiredAction = () => {
    sessionExpiredRef.current = true
    navigate('/login', { replace: true })
  }

  useEffect(() => {
    if (!isAuthenticated || telemetryConsent !== 'granted') {
      return
    }
    const controller = new AbortController()
    const source = location.pathname.startsWith('/admin')
      ? 'admin'
      : (location.pathname.startsWith('/login') ? 'auth' : 'app')
    sendVisitTelemetry({
      path: `${location.pathname}${location.search || ''}`,
      source,
      method: 'view',
      signal: controller.signal,
    })
    return () => controller.abort()
  }, [isAuthenticated, telemetryConsent, location.pathname, location.search])

  if (isLoading) {
    return <LoadingScreen />
  }

  if (connectionError) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">🔌</div>
          <h1 className="text-2xl font-bold text-white mb-4">
            Connection Error
          </h1>
          <p className="text-gray-400 mb-6">
            Unable to connect to the server. Please check your connection and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Retry Connection
          </button>
          <div className="mt-4 text-sm text-gray-500">
            Error: {connectionError}
          </div>
        </div>
      </div>
    )
  }

  return (
    <SocketProvider>
  <div className={`min-h-screen bg-gray-900 text-gray-100 ${showSessionExpired ? 'pt-16' : ''}`}>
        {showSessionExpired && (
          <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500/95 backdrop-blur border-b border-amber-400/60">
            <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3 text-amber-900">
              <div className="text-sm font-medium" role="status">
                Your session expired due to inactivity. Please sign back in to continue.
              </div>
              <button
                type="button"
                onClick={handleSessionExpiredAction}
                className="rounded-md bg-amber-900 px-3 py-1.5 text-sm font-semibold text-amber-100 shadow-sm transition hover:bg-amber-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-900 focus-visible:ring-offset-amber-500"
              >
                Sign back in
              </button>
            </div>
          </div>
        )}
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route path="/" element={<RequireAuth><Layout /></RequireAuth>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="terminal" element={<Terminal />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/:reportId" element={<ReportDetails />} />
            <Route path="settings" element={<Settings />} />
            <Route path="targets" element={<Targets />} />
            <Route path="usage" element={<Usage />} />
            <Route path="admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
        
        {/* Global notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1f2937',
              color: '#f9fafb',
              border: '1px solid #374151',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#f9fafb',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#f9fafb',
              },
            },
          }}
        />
        
        {/* Connection status indicator (hidden in production unless explicitly enabled) */}
        {showConnectionDebug && !isConnected && (
          <div className="fixed bottom-4 left-4 z-50">
            <div className="bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Disconnected</span>
            </div>
          </div>
        )}
        
        {/* Development tools (only in dev mode) */}
        {isDevBuild && (
          <div className="fixed bottom-4 right-4 z-50">
            <details className="bg-gray-800 border border-gray-600 rounded-lg p-2 text-xs">
              <summary className="cursor-pointer text-gray-400">
                Dev Tools
              </summary>
              <div className="mt-2 space-y-1 text-gray-300">
                <div>Connected: {isConnected ? '✅' : '❌'}</div>
                <div>Environment: {import.meta.env.MODE}</div>
                <div>Version: 1.0.0</div>
              </div>
            </details>
          </div>
        )}
      </div>
    </SocketProvider>
  )
}

export default App 
